/**
 * CRM Board Header
 *
 * Header with metrics, actions, and view toggles
 */

import { Plus, RefreshCw, Filter, TrendingUp, DollarSign, Target, Clock, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatCurrency } from '@/lib/utils';
import { DEFAULT_CURRENCY } from '../../constants';
import type { PipelineSummary } from '../../types';

interface CrmBoardHeaderProps {
  summary?: PipelineSummary;
  isLoading?: boolean;
  onRefresh?: () => void;
  onCreateOpportunity?: () => void;
  onToggleFilters?: () => void;
  hasActiveFilters?: boolean;
}

export function CrmBoardHeader({
  summary,
  isLoading,
  onRefresh,
  onCreateOpportunity,
  onToggleFilters,
  hasActiveFilters,
}: CrmBoardHeaderProps) {
  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4">
      {/* Title Row */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Sales Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage your opportunities through the sales process
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleFilters}
            className={hasActiveFilters ? 'border-primary text-primary' : ''}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 bg-primary text-white rounded-full w-4 h-4 text-xs flex items-center justify-center">
                !
              </span>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={onCreateOpportunity}>
            <Plus className="h-4 w-4 mr-2" />
            New Opportunity
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-4 gap-4">
        {/* Total Pipeline */}
        <MetricCard
          icon={<DollarSign className="h-4 w-4" />}
          label="Total Pipeline"
          tooltip="Sum of estimated values across all active opportunities"
          value={
            isLoading ? (
              <Skeleton className="h-6 w-24" />
            ) : (
              formatCurrency(summary?.total_pipeline_value || 0, DEFAULT_CURRENCY)
            )
          }
          subValue={
            summary?.weighted_pipeline_value
              ? `${formatCurrency(summary.weighted_pipeline_value, DEFAULT_CURRENCY)} weighted`
              : undefined
          }
          color="blue"
        />

        {/* Open Deals */}
        <MetricCard
          icon={<Target className="h-4 w-4" />}
          label="Open Opportunities"
          tooltip="Count of opportunities not yet converted or lost"
          value={
            isLoading ? <Skeleton className="h-6 w-12" /> : String(summary?.total_opportunities || 0)
          }
          subValue={
            summary?.avg_deal_size
              ? `${formatCurrency(summary.avg_deal_size, DEFAULT_CURRENCY)} avg`
              : undefined
          }
          color="violet"
        />

        {/* Conversion Rate */}
        <MetricCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Conversion Rate"
          tooltip="Opportunities converted to quotes out of all closed opportunities (won + lost)"
          value={
            isLoading ? (
              <Skeleton className="h-6 w-12" />
            ) : (
              `${((summary?.conversion_rate || 0) * 100).toFixed(1)}%`
            )
          }
          subValue="to quote"
          color="green"
        />

        {/* Avg Days in Pipeline */}
        <MetricCard
          icon={<Clock className="h-4 w-4" />}
          label="Avg Cycle Time"
          tooltip="Average days from opportunity creation to quote conversion"
          value={
            isLoading ? (
              <Skeleton className="h-6 w-12" />
            ) : (
              `${Math.round(summary?.avg_days_in_pipeline || 0)} days`
            )
          }
          subValue="prospect to quote"
          color="amber"
        />
      </div>
    </div>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  subValue?: string;
  tooltip?: string;
  color: 'blue' | 'violet' | 'green' | 'amber';
}

function MetricCard({ icon, label, value, subValue, tooltip, color }: MetricCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    violet: 'bg-violet-50 text-violet-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <Card className="p-3">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <p className="text-xs text-gray-500 truncate">{label}</p>
            {tooltip && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p>{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <p className="text-lg font-semibold text-gray-900">{value}</p>
          {subValue && <p className="text-xs text-gray-400 truncate">{subValue}</p>}
        </div>
      </div>
    </Card>
  );
}
